import { useState, useEffect, useMemo, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { appLabel } from '../../lib/appConstants';
import {
  ANNOUNCEMENT_TARGETS,
  deepLinkExamples,
  deepLinkPathList,
  isAppDeepLink,
  isKnownDeepLinkPath,
  schemePrefix,
} from '../../lib/deepLinks';
import { useDialogs } from '../../components/PromptDialog';
import {
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
} from './api';
import { MobilePreview } from './MobilePreview';
import { ReachStatsBlock } from './ReachStats';
import type { Announcement, AnnouncementAudience, AnnouncementType } from './types';

const TYPES: AnnouncementType[] = ['NEWS', 'UPDATE', 'PROMO', 'TECH', 'MAINTENANCE'];
const TYPE_LABEL: Record<AnnouncementType, string> = {
  NEWS: 'News',
  UPDATE: 'Update',
  PROMO: 'Aktion',
  TECH: 'Tech',
  MAINTENANCE: 'Wartung',
};

const AUDIENCES: { value: AnnouncementAudience; label: string }[] = [
  { value: 'ALL', label: 'Alle Nutzer' },
  { value: 'FREE_ONLY', label: 'Nur Free' },
  { value: 'PREMIUM_ONLY', label: 'Nur Pro (Premium & Premium+)' },
  { value: 'PREMIUM_PLUS_ONLY', label: 'Nur Premium+' },
];

interface FormState {
  title: string;
  body: string;
  type: AnnouncementType;
  imageUrl: string;
  ctaLabel: string;
  ctaUrl: string;
  priority: number;
  audience: AnnouncementAudience;
  minAppVersion: string;
  scheduledAt: string;
  expiresAt: string;
  isActive: boolean;
  dismissible: boolean;
  showOnce: boolean;
}

const EMPTY_STATE: FormState = {
  title: '',
  body: '',
  type: 'NEWS',
  imageUrl: '',
  ctaLabel: '',
  ctaUrl: '',
  priority: 0,
  audience: 'ALL',
  minAppVersion: '',
  scheduledAt: '',
  expiresAt: '',
  isActive: true,
  dismissible: true,
  showOnce: true,
};

function fromAnnouncement(a: Announcement): FormState {
  return {
    title: a.title,
    body: a.body,
    type: a.type,
    imageUrl: a.imageUrl ?? '',
    ctaLabel: a.ctaLabel ?? '',
    ctaUrl: a.ctaUrl ?? '',
    priority: a.priority,
    audience: a.audience,
    minAppVersion: a.minAppVersion ?? '',
    scheduledAt: a.scheduledAt ? toLocalInput(a.scheduledAt) : '',
    expiresAt: a.expiresAt ? toLocalInput(a.expiresAt) : '',
    isActive: a.isActive,
    dismissible: a.dismissible,
    showOnce: a.showOnce,
  };
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  // datetime-local braucht "YYYY-MM-DDTHH:mm" (ohne Sekunden, ohne Tz)
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
}

function toApiPayload(form: FormState) {
  return {
    title: form.title.trim(),
    body: form.body.trim(),
    type: form.type,
    imageUrl: form.imageUrl.trim() || null,
    ctaLabel: form.ctaLabel.trim() || null,
    ctaUrl: form.ctaUrl.trim() || null,
    priority: Number.isFinite(form.priority) ? form.priority : 0,
    audience: form.audience,
    minAppVersion: form.minAppVersion.trim() || null,
    scheduledAt: form.scheduledAt ? new Date(form.scheduledAt).toISOString() : null,
    expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
    isActive: form.isActive,
    dismissible: form.dismissible,
    showOnce: form.showOnce,
  };
}

interface EditorProps {
  existing: Announcement | null; // null = neu anlegen
  app: string; // Ziel-App (aus dem globalen Picker)
  onClose: () => void;
}

export function AnnouncementEditor({ existing, app, onClose }: EditorProps): JSX.Element {
  const queryClient = useQueryClient();
  const dialogs = useDialogs();
  const [form, setForm] = useState<FormState>(
    existing ? fromAnnouncement(existing) : EMPTY_STATE,
  );
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!existing;

  // Deep-Link-Hinweise haengen an der Ziel-App: das Schema ist pro App anders
  // (currivium -> curalearn://, psychotherapie -> currivium-hpp://), und ein
  // Link mit fremdem Schema oder unbekanntem Pfad wird auf dem Geraet still
  // verworfen — AnnouncementSheet oeffnet nur, wenn canOpenURL() true sagt.
  const ctaPrefix = schemePrefix(app);
  const ctaExamples = useMemo(
    () => deepLinkExamples(app, ANNOUNCEMENT_TARGETS),
    [app],
  );
  const ctaPathList = useMemo(() => deepLinkPathList(ANNOUNCEMENT_TARGETS), []);
  const ctaUrl = form.ctaUrl.trim();
  const ctaKind: 'none' | 'deeplink' | 'unknown-path' | 'external' | 'foreign-scheme' = !ctaUrl
    ? 'none'
    : isAppDeepLink(ctaUrl, app)
      ? isKnownDeepLinkPath(ctaUrl, app, ANNOUNCEMENT_TARGETS)
        ? 'deeplink'
        : 'unknown-path'
      : /^https?:\/\//.test(ctaUrl)
        ? 'external'
        : 'foreign-scheme';

  useEffect(() => {
    if (existing) setForm(fromAnnouncement(existing));
    else setForm(EMPTY_STATE);
  }, [existing]);

  const createMutation = useMutation({
    mutationFn: () => createAnnouncement(toApiPayload(form), app),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      onClose();
    },
    onError: (err) => setError((err as Error).message),
  });

  const updateMutation = useMutation({
    mutationFn: () => updateAnnouncement(existing!.id, toApiPayload(form), app),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      onClose();
    },
    onError: (err) => setError((err as Error).message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteAnnouncement(existing!.id, app),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      onClose();
    },
    onError: (err) => setError((err as Error).message),
  });

  const submitting = createMutation.isPending || updateMutation.isPending;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.title.trim()) {
      setError('Titel ist Pflicht.');
      return;
    }
    if (!form.body.trim()) {
      setError('Body ist Pflicht.');
      return;
    }
    if (isEdit) updateMutation.mutate();
    else createMutation.mutate();
  }

  async function handleDelete() {
    if (!existing) return;
    const ok = await dialogs.confirm({
      title: 'Announcement löschen?',
      message: `Announcement "${existing.title}" wirklich löschen?`,
      confirmLabel: 'Ja, löschen',
      destructive: true,
    });
    if (!ok) return;
    deleteMutation.mutate();
  }

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-0 sm:p-4">
      <div
        className="grid h-dvh w-full max-w-[1100px] grid-cols-1 overflow-hidden rounded-none border-0 shadow-2xl sm:h-[92vh] sm:rounded-2xl sm:border lg:grid-cols-[1fr_360px]"
        style={{
          background: darkTheme.colors.bgSurface,
          borderColor: darkTheme.colors.borderSubtle,
        }}
      >
        {/* Form — min-h-0 erzwingt, dass Grid-Cell-Inhalt scrollt statt
            unbegrenzt zu wachsen (CSS-Grid-Default ist min-height:auto) */}
        <form onSubmit={handleSubmit} className="min-h-0 overflow-y-auto p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-text-primary">
              {isEdit ? 'Announcement bearbeiten' : 'Neues Announcement'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md p-1.5 text-text-secondary hover:bg-bg-elevated sm:min-h-0 sm:min-w-0"
              aria-label="Schließen"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            {/* Type pills */}
            <Field label="Typ">
              <div className="flex flex-wrap gap-1.5">
                {TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => update('type', t)}
                    className="rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors"
                    style={{
                      background: form.type === t ? darkTheme.colors.accentSubtle : 'transparent',
                      color: form.type === t ? darkTheme.colors.accentLight : darkTheme.colors.textSecondary,
                      border: `1px solid ${form.type === t ? darkTheme.colors.accent : darkTheme.colors.borderSubtle}`,
                    }}
                  >
                    {TYPE_LABEL[t]}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Titel" hint={`${form.title.length}/60`}>
              <input
                type="text"
                maxLength={60}
                value={form.title}
                onChange={(e) => update('title', e.target.value)}
                className={inputClass}
                style={{ borderColor: darkTheme.colors.borderSubtle }}
                required
              />
            </Field>

            <Field label="Body (Markdown)" hint={`${form.body.length} Zeichen`}>
              <textarea
                rows={5}
                value={form.body}
                onChange={(e) => update('body', e.target.value)}
                className={inputClass}
                style={{ borderColor: darkTheme.colors.borderSubtle }}
                required
              />
            </Field>

            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Image-URL (optional)">
                <input
                  type="url"
                  value={form.imageUrl}
                  onChange={(e) => update('imageUrl', e.target.value)}
                  placeholder="https://image.cura-learn.app/…"
                  className={inputClass}
                  style={{ borderColor: darkTheme.colors.borderSubtle }}
                />
              </Field>
              <Field label="Priority" hint="höher = zuerst sichtbar (default 0)">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="-?[0-9]*"
                  value={String(form.priority)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^\d-]/g, '');
                    if (raw === '' || raw === '-') {
                      update('priority', 0);
                      return;
                    }
                    const n = parseInt(raw, 10);
                    update('priority', Number.isFinite(n) ? n : 0);
                  }}
                  className={inputClass}
                  style={{ borderColor: darkTheme.colors.borderSubtle }}
                />
              </Field>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <Field label="CTA-Label (optional)">
                <input
                  type="text"
                  value={form.ctaLabel}
                  onChange={(e) => update('ctaLabel', e.target.value)}
                  placeholder="Jetzt ansehen"
                  className={inputClass}
                  style={{ borderColor: darkTheme.colors.borderSubtle }}
                />
              </Field>
              <Field label="CTA-URL" hint={`https:// oder ${ctaPrefix}`}>
                <input
                  type="text"
                  value={form.ctaUrl}
                  onChange={(e) => update('ctaUrl', e.target.value)}
                  placeholder={`${ctaPrefix}learn/Lesson/abc123`}
                  className={inputClass}
                  style={{ borderColor: darkTheme.colors.borderSubtle }}
                />
                {ctaKind === 'deeplink' && (
                  <p className="mt-1 text-[10px] text-text-tertiary">
                    Deep-Link in {appLabel(app)}. Beispiele:{' '}
                    {ctaExamples.map((ex, i) => (
                      <span key={ex}>
                        {i > 0 ? ', ' : ''}
                        <code className="text-accent-light">{ex}</code>
                      </span>
                    ))}
                  </p>
                )}
                {ctaKind === 'unknown-path' && (
                  <p className="mt-1 text-[10px]" style={{ color: darkTheme.colors.warning }}>
                    Dieser Pfad ist keine Route der App — der CTA bleibt auf dem Gerät
                    wirkungslos (kein Fehler, kein Hinweis). Gültig:{' '}
                    <code>{ctaPathList}</code>
                  </p>
                )}
                {ctaKind === 'external' && (
                  <p className="mt-1 text-[10px] text-text-tertiary">
                    Externer Link — öffnet im In-App-Browser.
                  </p>
                )}
                {ctaKind === 'foreign-scheme' && (
                  <p className="mt-1 text-[10px]" style={{ color: darkTheme.colors.warning }}>
                    Weder ein http(s)-Link noch ein Deep-Link für {appLabel(app)}
                    {' '}(<code>{ctaPrefix}</code>) — der CTA bleibt auf dem Gerät
                    wirkungslos.
                  </p>
                )}
              </Field>
            </div>

            <Field label="Zielgruppe">
              <div className="flex flex-wrap gap-1.5">
                {AUDIENCES.map((a) => (
                  <button
                    key={a.value}
                    type="button"
                    onClick={() => update('audience', a.value)}
                    className="rounded-full px-3 py-1 text-xs"
                    style={{
                      background:
                        form.audience === a.value ? darkTheme.colors.accentSubtle : 'transparent',
                      color:
                        form.audience === a.value ? darkTheme.colors.accentLight : darkTheme.colors.textSecondary,
                      border: `1px solid ${
                        form.audience === a.value ? darkTheme.colors.accent : darkTheme.colors.borderSubtle
                      }`,
                    }}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </Field>

            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Sichtbar ab" hint="leer = sofort">
                <input
                  type="datetime-local"
                  value={form.scheduledAt}
                  onChange={(e) => update('scheduledAt', e.target.value)}
                  className={inputClass}
                  style={{ borderColor: darkTheme.colors.borderSubtle }}
                />
              </Field>
              <Field label="Sichtbar bis" hint="leer = unbegrenzt">
                <input
                  type="datetime-local"
                  value={form.expiresAt}
                  onChange={(e) => update('expiresAt', e.target.value)}
                  className={inputClass}
                  style={{ borderColor: darkTheme.colors.borderSubtle }}
                />
              </Field>
            </div>

            <Field label="Min. App-Version (SemVer, optional)">
              <input
                type="text"
                value={form.minAppVersion}
                onChange={(e) => update('minAppVersion', e.target.value)}
                placeholder="1.2.0"
                className={inputClass}
                style={{ borderColor: darkTheme.colors.borderSubtle }}
              />
            </Field>

            <div className="flex flex-wrap items-center gap-4 pt-1">
              <Toggle
                label="Aktiv"
                checked={form.isActive}
                onChange={(v) => update('isActive', v)}
              />
              <Toggle
                label="Dismissible"
                checked={form.dismissible}
                onChange={(v) => update('dismissible', v)}
              />
              <Toggle
                label="Show once"
                checked={form.showOnce}
                onChange={(v) => update('showOnce', v)}
              />
            </div>
          </div>

          {error && (
            <p className="mt-4 rounded-md p-2 text-sm" style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}>
              {error}
            </p>
          )}

          <div className="mt-5 flex items-center justify-between gap-2 border-t pt-4" style={{ borderColor: darkTheme.colors.borderSubtle }}>
            <div>
              {isEdit && (
                <button
                  type="button"
                  onClick={() => void handleDelete()}
                  disabled={deleteMutation.isPending}
                  className="rounded-md px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                  style={{ background: darkTheme.colors.danger }}
                >
                  {deleteMutation.isPending ? 'Lösche …' : 'Löschen'}
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md px-3 py-1.5 text-xs text-text-secondary hover:bg-bg-elevated"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-md px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                style={{ background: darkTheme.colors.accent }}
              >
                {submitting ? 'Speichert …' : isEdit ? 'Speichern' : 'Veröffentlichen'}
              </button>
            </div>
          </div>
        </form>

        {/* Live Mobile Preview */}
        <aside
          className="hidden min-h-0 overflow-y-auto border-l p-5 lg:block"
          style={{
            background: darkTheme.colors.bgBase,
            borderColor: darkTheme.colors.borderSubtle,
          }}
        >
          <MobilePreview
            title={form.title}
            body={form.body}
            type={form.type}
            imageUrl={form.imageUrl.trim() || null}
            ctaLabel={form.ctaLabel.trim() || null}
            ctaUrl={form.ctaUrl.trim() || null}
            dismissible={form.dismissible}
          />
          {existing && (
            <div
              className="mt-6 border-t pt-4"
              style={{ borderColor: darkTheme.colors.borderSubtle }}
            >
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
                Reach
              </div>
              <ReachStatsBlock announcementId={existing.id} app={app} />
            </div>
          )}
        </aside>
      </div>

      {/* Außerhalb des Editor-<form>: ModalShell rendert selbst ein <form> —
          verschachtelte Forms würden das Submit des Editors mitfeuern. */}
      {dialogs.dialog}
    </div>
  );
}

const inputClass =
  'w-full rounded-md border bg-bg-base px-3 py-2 text-sm text-text-primary outline-none focus:border-accent';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-text-secondary">{label}</span>
        {hint && <span className="text-[10px] text-text-tertiary">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-xs text-text-secondary">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
